import React, { useState, useEffect } from 'react';
import { ArrowRight, Star, ShoppingBag } from 'lucide-react';
import { motion } from 'framer-motion';

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      image: "/hero 1.jpg",
      title: "Elegant Indian Ethnic Wear",
      subtitle: "Traditional craftsmanship meets contemporary style",
      cta: "Explore Collection"
    },
    {
      image: "/hero 2.jpg",
      title: "Luxury Silk & Pattu Fabrics",
      subtitle: "Premium quality materials for exceptional comfort",
      cta: "Discover Fabrics"
    },
    {
      image: "/hero 3.jpg",
      title: "Coordinated Family Styling",
      subtitle: "Beautiful outfits for every special occasion",
      cta: "View Collections"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <section id="home" className="relative min-h-[500px] h-[85vh] md:h-screen overflow-hidden">
      {/* Slideshow Background */}
      <div className="absolute inset-0">
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
          >
            <div
              className="w-full h-full bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url("${slide.image}")` }}
            >
              <div className="absolute inset-0 bg-black/50"></div>
            </div>
          </div>
        ))}
      </div>

      {/* Content Overlay */}
      <div className="relative z-10 h-full flex items-center pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="text-center text-white">
            <div className="mb-4 md:mb-6">
              <div className="flex justify-center items-center gap-1.5 mb-3 md:mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 md:w-5 md:h-5 fill-brand-gold text-brand-gold" />
                ))}
              </div>
              <p className="text-xs md:text-sm font-bold text-brand-pink uppercase tracking-[0.2em] mb-1">
                London&apos;s Contemporary Fashion
              </p>
            </div>

            <motion.h1
              key={`title-${currentSlide}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold mb-4 md:mb-6 leading-tight max-w-4xl mx-auto tracking-tight"
            >
              {slides[currentSlide].title}
            </motion.h1>

            <motion.p
              key={`subtitle-${currentSlide}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
              className="text-base md:text-xl lg:text-2xl mb-8 text-white/90 max-w-2xl mx-auto px-4 font-medium leading-relaxed"
            >
              {slides[currentSlide].subtitle}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center px-4"
            >
              <a
                href="#collections"
                className="w-full sm:w-auto bg-gradient-to-r from-brand-rose to-brand-dusty hover:from-brand-dusty hover:to-brand-rose text-white px-8 md:px-10 py-3.5 md:py-4 rounded-full font-bold transition-all duration-500 transform hover:scale-105 flex items-center justify-center gap-2 text-sm md:text-base shadow-xl shadow-brand-rose/25"
              >
                <ShoppingBag className="w-4 h-4 md:w-5 md:h-5" />
                {slides[currentSlide].cta}
                <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
              </a>

              <a
                href="https://wa.me/447721540241"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20 text-white px-8 md:px-10 py-3.5 md:py-4 rounded-full font-bold transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2 text-sm md:text-base shadow-xl"
              >
                Get Styling Advice
              </a>
            </motion.div>

            {/* Brand Trust Indicators - Adjusted for Mobile Grid */}
            <div className="mt-10 md:mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 max-w-5xl mx-auto px-4">
              <div className="text-center p-4 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 hover:bg-white/10 transition-colors duration-300 group">
                <div className="text-2xl md:text-4xl font-bold text-brand-pink group-hover:scale-110 transition-transform duration-300">500+</div>
                <div className="text-[10px] md:text-sm text-gray-300 uppercase tracking-[0.15em] font-medium mt-1">Happy Clients</div>
              </div>
              <div className="text-center p-4 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 hover:bg-white/10 transition-colors duration-300 group">
                <div className="text-2xl md:text-4xl font-bold text-brand-pink group-hover:scale-110 transition-transform duration-300">100+</div>
                <div className="text-[10px] md:text-sm text-gray-300 uppercase tracking-[0.15em] font-medium mt-1">Designs</div>
              </div>
              <div className="text-center p-4 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 hover:bg-white/10 transition-colors duration-300 group">
                <div className="text-2xl md:text-4xl font-bold text-brand-pink group-hover:scale-110 transition-transform duration-300">10+</div>
                <div className="text-[10px] md:text-sm text-gray-300 uppercase tracking-[0.15em] font-medium mt-1">Experience</div>
              </div>
              <div className="text-center p-4 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 hover:bg-white/10 transition-colors duration-300 group">
                <div className="text-2xl md:text-4xl font-bold text-brand-pink group-hover:scale-110 transition-transform duration-300">24/7</div>
                <div className="text-[10px] md:text-sm text-gray-300 uppercase tracking-[0.15em] font-medium mt-1">Support</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-20">
        <div className="flex space-x-3">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-2 rounded-full transition-all duration-500 ${index === currentSlide
                ? 'bg-brand-rose w-10 shadow-lg shadow-brand-rose/50'
                : 'bg-white/30 w-3 hover:bg-white/50'
                }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all duration-300"
        aria-label="Previous slide"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all duration-300"
        aria-label="Next slide"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 animate-bounce">
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
      </div>
    </section>
  );
};

export default Hero;
